import React from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Breadcrumbs, Link, Stack } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountTree as MappingIcon,
  Assessment as ProfileIcon,
  Description as LogsIcon,
  ViewQuilt as WorkspaceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useSSEAwareQuery } from '../hooks/useSSEAwareQuery';
import { featureFlags } from '../config';

import { useFeatureFlags } from '../context/FeatureFlagContext';
import { FeatureName } from '../types/featureNames';
import { getBatch } from '../services/batchApi';
import { useLayoutFrame } from '../context/LayoutFrameContext';
import { useActiveSessionForBatch } from '../hooks/useWorkspaceSession';
import { usesMappingEditorTab } from '../utils/mappingRunStatus';
import DeletedResourceNotice from '../components/common/DeletedResourceNotice';
import { deletedBatchMessage, getDeletedResourceMessage, isDeletedResourceError } from '../utils/deletedResource';


const BatchLayout: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeature } = useFeatureFlags();
  const { secondaryNavHidden, appBarHidden } = useLayoutFrame();

  // Logs tab visibility is driven by the tenant-level 'show_logs' feature flag
  const canViewLogs = hasFeature(FeatureName.SHOW_LOGS);

  // Fetch batch to determine if Mapping tab should be visible
  const {
    data: batch,
    error: batchError,
    isError: isBatchError,
    isLoading: isBatchLoading,
  } = useSSEAwareQuery(
    ['batch', batchId],
    () => getBatch(batchId!),
    {
      enabled: !!batchId,
      pollingInterval: 10_000,
      ssePollingInterval: 30_000,
      staleTime: 5000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => !isDeletedResourceError(error) && failureCount < 2,
    },
  );

  const batchDeleted = isBatchError && isDeletedResourceError(batchError);

  // Hide Mapping tab for agent-driven flows (iterative dialog or chat instructions).
  // Only the V2 engine path (mapping_mode === 'engine') uses the Mapping editor tab.
  const { data: session, isLoading: sessionLoading } = useActiveSessionForBatch(batchId);

  const MAPPING_VISIBLE_STATUSES = new Set([
    'mapped', 'reviewing', 'approved', 'transforming', 'loading', 'completed',
  ]);
  const sessionMeta = session?.workspace_meta;
  const hasMappingIndicators = Boolean(
    batch && (MAPPING_VISIBLE_STATUSES.has(batch.status) || (batch.mapping_count ?? 0) > 0),
  );
  const showMappingTab = Boolean(
    hasMappingIndicators &&
    !sessionLoading &&
    usesMappingEditorTab(sessionMeta),
  );


  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.endsWith('/mapping')) return 'mapping';
    if (path.endsWith('/workspace')) return 'workspace';
    if (path.endsWith('/agent')) return 'workspace';
    if (path.endsWith('/overview')) return 'overview';
    if (path.endsWith('/profile')) return 'profile';
    if (path.endsWith('/schedule')) return 'schedule';
    if (path.endsWith('/prompts') || path.endsWith('/logs')) return 'prompts';
    if (featureFlags.workspaceExperiment) return 'workspace';
    return 'overview';
  };

  // Auto-redirect to workspace on initial load (bare /batches/:id path only)
  React.useEffect(() => {
    const path = location.pathname;
    const barePath = `/batches/${batchId}`;
    // Only redirect the bare path — not /overview, /workspace, etc.
    if (featureFlags.workspaceExperiment && (path === barePath || path === `${barePath}/`)) {
      navigate(`/batches/${batchId}/workspace`, { replace: true });
    }
  }, [location.pathname, batchId, navigate]);

  // Iterative mapping has no Mapping tab — redirect only once session confirms agent-driven mode.
  React.useEffect(() => {
    if (
      !batchId ||
      sessionLoading ||
      !location.pathname.endsWith('/mapping') ||
      !session
    ) {
      return;
    }
    if (!usesMappingEditorTab(session.workspace_meta)) {
      navigate(`/batches/${batchId}/workspace`, { replace: true });
    }
  }, [location.pathname, session, sessionLoading, batchId, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === 'overview') {
      navigate(`/batches/${batchId}/overview`);
    } else if (newValue === 'workspace') {
      navigate(`/batches/${batchId}/workspace`);
    } else if (newValue === 'mapping') {
      navigate(`/batches/${batchId}/mapping`);
    } else if (newValue === 'profile') {
      navigate(`/batches/${batchId}/profile`);
    } else if (newValue === 'schedule') {
      navigate(`/batches/${batchId}/schedule`);
    } else if (newValue === 'prompts') {
      navigate(`/batches/${batchId}/prompts`);
    }
  };

  const batchOutletFullBleed = appBarHidden && secondaryNavHidden;

  if (batchDeleted) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%',
          minHeight: '100%',
        }}
      >
        <DeletedResourceNotice
          title="Batch unavailable"
          message={getDeletedResourceMessage(batchError) || deletedBatchMessage()}
          backLabel="Back to transformations"
          backTo="/batches"
        />
      </Box>
    );
  }

  if (isBatchLoading && !batch) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Context Header — breadcrumbs + batch tabs */}
      {!secondaryNavHidden && (
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          height: 48,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ overflow: 'hidden' }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}>
            <Link
              color="inherit"
              href="/batches"
              onClick={(e) => {
                e.preventDefault();
                navigate('/batches');
              }}
              sx={{ display: 'flex', alignItems: 'center', fontSize: 13, whiteSpace: 'nowrap' }}
            >
              Transformations
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {batch?.batch_name || batch?.files?.[0] || batchId?.slice(0, 8) || ''}
            </Typography>
            {getCurrentTab() === 'mapping' && (
              <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                Mapping
              </Typography>
            )}
          </Breadcrumbs>
        </Stack>

        <Tabs
          value={getCurrentTab()}
          onChange={handleTabChange}
          sx={{
            minHeight: 48,
            '& .MuiTabs-indicator': { top: 46 }, // Move indicator to bottom
            marginRight: 'auto', // Push to left if needed, or remove for center
          }}
        >
          <Tab icon={<DashboardIcon fontSize="small" />} iconPosition="start" label="Overview" value="overview" sx={{ minHeight: 48, py: 0 }} />
          {featureFlags.workspaceExperiment && (
            <Tab icon={<WorkspaceIcon fontSize="small" />} iconPosition="start" label="Workspace" value="workspace" sx={{ minHeight: 48, py: 0 }} />
          )}
          {showMappingTab && (
            <Tab
              icon={<MappingIcon fontSize="small" />}
              iconPosition="start"
              label="Mapping"
              value="mapping"
              sx={{ minHeight: 48, py: 0 }}
            />
          )}
          <Tab icon={<ProfileIcon fontSize="small" />} iconPosition="start" label="Profile" value="profile" sx={{ minHeight: 48, py: 0 }} />
          <Tab icon={<ScheduleIcon fontSize="small" />} iconPosition="start" label="Schedule" value="schedule" sx={{ minHeight: 48, py: 0 }} />
          {featureFlags.canvasExperiment && canViewLogs && (
            <Tab icon={<LogsIcon fontSize="small" />} iconPosition="start" label="Logs" value="prompts" sx={{ minHeight: 48, py: 0 }} />
          )}
        </Tabs>
      </Box>
      )}

      {/* Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: batchOutletFullBleed ? 'hidden' : 'auto',
          position: 'relative',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default BatchLayout;
